"use client";

/**
 * @file 结果显示组件
 * @description 显示原始宠物图片和拟人化结果的组件
 */

import Image from 'next/image';
import Button from '@/components/common/Button';

/**
 * 结果显示组件属性接口
 */
interface ResultDisplayProps {
  /** 原始宠物图片 */
  originalImage: string | null;
  /** 拟人化结果图片 */
  resultImage: string | null;
  /** 是否正在处理中 */
  isProcessing: boolean;
  /** 选择的风格 */
  selectedStyle: {
    id: string;
    name: string;
    description: string;
  };
}

/**
 * 结果显示组件
 * @param {ResultDisplayProps} props - 组件属性
 * @returns {JSX.Element} 结果显示组件
 */
export default function ResultDisplay({
  originalImage,
  resultImage,
  isProcessing,
  selectedStyle,
}: ResultDisplayProps) {
  /**
   * 下载结果图片
   */
  const handleDownload = () => {
    if (!resultImage) return;

    // 创建链接元素
    const link = document.createElement('a');
    link.href = resultImage;
    link.download = `pet-to-human-${selectedStyle.id}-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 如果没有原始图片，显示上传提示
  if (!originalImage) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <div className="w-24 h-24 mb-4 text-gray-300 dark:text-gray-600">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
          Waiting for Pet Photo
        </h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md">
          Please upload a pet photo, select your preferred style, and click the &quot;Start Transformation&quot; button to begin.
        </p>
      </div>
    );
  }

  // 如果正在处理，显示加载动画
  if (isProcessing) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <div className="w-16 h-16 mb-4">
          <svg className="animate-spin text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
          Processing...
        </h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md">
          Our AI is analyzing your pet photo and generating a human portrait in {selectedStyle.name} style, please wait...
        </p>
      </div>
    );
  }

  // 如果有结果图片，显示对比视图
  if (resultImage) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex-1 grid grid-cols-2 gap-4">
          {/* 原始图片 */}
          <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="absolute top-0 left-0 bg-gray-100 dark:bg-gray-700 text-xs px-2 py-1 rounded-br-lg">
              Original Pet Photo
            </div>
            <div className="w-full h-full relative">
              <Image
                src={originalImage}
                alt="Original pet photo"
                fill
                className="object-contain"
              />
            </div>
          </div>

          {/* 结果图片 */}
          <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
            <div className="absolute top-0 left-0 bg-blue-100 dark:bg-blue-900/30 text-xs px-2 py-1 rounded-br-lg">
              {selectedStyle.name} Result
            </div>
            <div className="w-full h-full relative">
              <Image
                src={resultImage}
                alt="Transformation result"
                fill
                className="object-contain"
              />
            </div>
          </div>
        </div>

        {/* 下载按钮 */}
        <div className="mt-4">
          <Button onClick={handleDownload} variant="primary" className="w-full">
            Download Result
          </Button>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
            Image will be downloaded to your device in PNG format
          </p>
        </div>
      </div>
    );
  }

  // 如果有原始图片但没有结果，显示等待处理提示
  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
        <div className="w-full h-48 relative mb-4 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
          <Image
            src={originalImage}
            alt="Original pet photo"
            fill
            className="object-contain"
          />
        </div>
        <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
          Ready
        </h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-md">
          Your pet photo has been uploaded and {selectedStyle.name} style is selected. Click the &quot;Start Transformation&quot; button to begin.
        </p>
      </div>
    </div>
  );
}
